import React, { useState } from "react";
import MaterialListSelect from "./MaterialListSelect";
import MaterialGroupSelect from "./MaterialGroupSelect";
import MaterialLocationSelect from "./MaterialLocationSelect";
import { toast } from "react-toastify";
import LoadingOverlay from "./LoadingOverlay";
import axios from "axios";
import { formatNumber } from "../utils/numbers";

const VIEW_OPTIONS = [
  { value: "0", label: "Show All" },
  { value: "1", label: "Search by Product Code / Name" },
  { value: "2", label: "Search by Group" },
];

const thCls =
  "px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-r last:border-r-0";
const tdCls =
  "px-4 py-2.5 text-xs text-gray-700 border-r last:border-r-0 whitespace-nowrap";

function MaterialCurrentStock() {
  const [viewType, setViewType] = useState("0");
  const [selectedMatList, setSelectedMatList] = useState(null);
  const [selectedMatGroup, setSelectedMatGroup] = useState(null);
  const [productLocation, setProductLocation] = useState("");
  const [showEmpty, setShowEmpty] = useState(true);
  const [currentStock, setCurrentStock] = useState([]);
  const [currentGroupedStock, setCurrentGroupedStock] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const formatRecord = (data) => {
    if (!data || data.length === 0) return {};
    return data.reduce((acc, item) => {
      const g = item.group_name;
      if (!acc[g]) acc[g] = [];
      acc[g].push(item);
      return acc;
    }, {});
  };

  const handleViewTypeChange = (e) => {
    setViewType(e.target.value);
    setCurrentStock([]);
    setCurrentGroupedStock({});
    setSelectedMatList(null);
    setSelectedMatGroup(null);
    setSearched(false);
  };

  const getallCurrentStock = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        "https://eps.elkomprintama.com/get/api/store/currentallstock",
      );
      const data = showEmpty
        ? res.data.result
        : res.data.result.filter((i) => i.current_stock > 0);
      setCurrentGroupedStock(formatRecord(data));
      setSearched(true);
    } catch {
      toast.error("Failed to fetch stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentStockByGroupId = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/currentallstockbygroup/${selectedMatGroup}`,
      );
      const data = showEmpty
        ? res.data.result
        : res.data.result.filter((i) => i.current_stock > 0);
      setCurrentStock(data);
      setSearched(true);
    } catch {
      toast.error("Failed to fetch group stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const getCurrentStockById = async () => {
    setIsLoading(true);
    try {
      const res = await axios.get(
        `https://eps.elkomprintama.com/get/api/store/currentallstockraw/${selectedMatList}`,
      );
      const data = res.data.result;
      setCurrentStock(
        typeof data === "object" && !Array.isArray(data) ? [data] : data,
      );
      setSearched(true);
    } catch {
      toast.error("Failed to fetch stock.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (viewType === "1" && !selectedMatList) {
      toast.warning("Please select a product.");
      return;
    }
    if (viewType === "2" && !selectedMatGroup) {
      toast.warning("Please select a group.");
      return;
    }
    if (viewType === "0") getallCurrentStock();
    else if (viewType === "1") getCurrentStockById();
    else if (viewType === "2") getCurrentStockByGroupId();
  };

  const groupEntries = Object.entries(currentGroupedStock);
  const totalItems =
    viewType === "0"
      ? groupEntries.reduce((s, [, items]) => s + items.length, 0)
      : currentStock.length;

  const stockBadge = (qty) => {
    if (qty <= 0) return "bg-red-100 text-red-700 font-bold";
    if (qty < 10) return "bg-yellow-100 text-yellow-700 font-semibold";
    return "text-gray-800 font-medium";
  };

  return (
    <div className="flex flex-col" style={{ maxHeight: "calc(100vh - 80px)" }}>
      {isLoading && <LoadingOverlay message="Loading…" />}

      {/* Header */}
      <div className="w-full rounded-lg bg-cyan-700 mb-4 flex-shrink-0">
        <label className="block uppercase text-white tracking-wide text-5xl text-center font-bold py-2">
          Current Stock
        </label>
      </div>

      {/* Body card */}
      <div className="flex-1 min-h-0 flex flex-col bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        {/* Controls */}
        <form
          onSubmit={handleSubmit}
          className="flex-shrink-0 px-6 py-5 border-b border-gray-100 space-y-4"
        >
          {/* View type + Show Empty toggle in one row */}
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-56">
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                View Mode
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-cyan-400 bg-white"
                value={viewType}
                onChange={handleViewTypeChange}
              >
                {VIEW_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            {viewType !== "1" && (
              <label className="flex items-center gap-2 cursor-pointer pb-1">
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={showEmpty}
                    onChange={() => setShowEmpty((v) => !v)}
                  />
                  <div className="w-10 h-6 rounded-full bg-gray-300 peer-checked:bg-cyan-600 transition-colors" />
                  <div className="absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                </div>
                <span className="text-sm text-gray-600">Show empty stock</span>
              </label>
            )}
          </div>

          {/* Conditional selectors */}
          {viewType === "1" && (
            <div className="space-y-3">
              <MaterialLocationSelect onChangeList={setProductLocation} />
              <MaterialListSelect
                onChangeList={(Arr) => setSelectedMatList(Arr[0])}
                onOffSwitch={viewType}
                location_id={productLocation}
              />
            </div>
          )}

          {viewType === "2" && (
            <MaterialGroupSelect onChangeList={setSelectedMatGroup} />
          )}

          <button
            type="submit"
            className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-cyan-700 hover:bg-cyan-800 transition"
          >
            Check Stock
          </button>
        </form>

        {/* Table */}
        <div className="flex-1 min-h-0 overflow-auto">
          {!searched ? (
            <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
              Select a view mode and click Check Stock
            </div>
          ) : totalItems === 0 ? (
            <div className="h-full flex items-center justify-center text-gray-400 italic text-sm">
              No stock records found
            </div>
          ) : (
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-gray-700 text-white text-xs uppercase">
                <tr>
                  <th className={thCls}>No</th>
                  <th className={thCls}>Product Name</th>
                  <th className={thCls}>Product Code</th>
                  <th className={thCls}>Current Stock</th>
                  <th className={thCls}>Unit</th>
                </tr>
              </thead>
              <tbody>
                {viewType === "0"
                  ? groupEntries.map(([groupName, groupItems], gi) => (
                      <React.Fragment key={`g-${gi}`}>
                        <tr className="bg-cyan-700 border-t-4 border-cyan-400">
                          <td
                            colSpan={5}
                            className="px-4 py-2 text-xs font-bold text-white uppercase tracking-widest"
                          >
                            {groupName} · {groupItems.length} item
                            {groupItems.length !== 1 ? "s" : ""}
                          </td>
                        </tr>
                        {groupItems.map((data, i) => (
                          <tr
                            key={`c-${gi}-${i}`}
                            className={`border-b hover:bg-cyan-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                          >
                            <td className={`${tdCls} text-gray-400`}>
                              {i + 1}
                            </td>
                            <td
                              className={`${tdCls} font-medium text-gray-800`}
                            >
                              {data.product_name}
                            </td>
                            <td className={tdCls}>{data.product_code}</td>
                            <td className="px-4 py-2.5 border-r whitespace-nowrap">
                              <span
                                className={`text-xs px-2 py-0.5 rounded ${stockBadge(data.current_stock)}`}
                              >
                                {formatNumber(data.current_stock)}
                              </span>
                            </td>
                            <td className={tdCls}>{data.unit}</td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))
                  : currentStock.map((data, i) => (
                      <tr
                        key={`cs-${i}`}
                        className={`border-b hover:bg-cyan-50 ${i % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
                      >
                        <td className={`${tdCls} text-gray-400`}>{i + 1}</td>
                        <td className={`${tdCls} font-medium text-gray-800`}>
                          {data.product_name}
                        </td>
                        <td className={tdCls}>{data.product_code}</td>
                        <td className="px-4 py-2.5 border-r whitespace-nowrap">
                          <span
                            className={`text-xs px-2 py-0.5 rounded ${stockBadge(data.current_stock)}`}
                          >
                            {formatNumber(data.current_stock)}
                          </span>
                        </td>
                        <td className={tdCls}>{data.unit}</td>
                      </tr>
                    ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer count */}
        {searched && totalItems > 0 && (
          <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 bg-gray-50">
            <span className="text-xs text-gray-400">
              {totalItems} record{totalItems !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default MaterialCurrentStock;
